import { unescapeMarkdown } from "./markdown-escape.js";

/**
 * How long a question is, measured the way the backend measures it before it cuts the
 * question off at AiChat:MaxMessageLength (AiChatContextBuilder): trimmed, with every line
 * break and tab counting as one character. The backend keeps them and only folds "\r\n" into
 * "\n"; the composer's plain-mode editor already gives a bare "\n" per line break, so that
 * part matches.
 *
 * What it hands over is its markdown serialization, though: `**vet**` comes out as
 * `\*\*vet\*\*`, and the submit path strips those backslashes with unescapeMarkdown()
 * before the backend sees the question. Counting the raw serialization would therefore
 * refuse a punctuation-heavy question that fits once sent, so the same normalization runs
 * here first and the count is the length of what actually leaves.
 */
export function countMessageCharacters(content: string): number {
  return unescapeMarkdown(content).trim().length;
}

/**
 * Makes afas-comment-form count like countMessageCharacters. Its own `characterCount`
 * getter takes the raw getContent(), escapes included, so `\## ` counts one more than
 * leaves (#373); its counter, send button and submit check all read that getter.
 */
export function useMessageCharacterCount(form: { getContent?: () => string }): void {
  Object.defineProperty(form, "characterCount", {
    configurable: true,
    get: () => countMessageCharacters(form.getContent?.() ?? ""),
  });
}

export function isMessageTooLong(length: number, maxLength: number): boolean {
  return maxLength > 0 && length > maxLength;
}

/**
 * Fills the `{length}` and `{max}` placeholders of the too-long label. Plain digits on
 * purpose: a locale-formatted "2.150" reads as a decimal to half the visitors.
 */
export function formatMessageTooLong(
  template: string,
  length: number,
  maxLength: number,
): string {
  return template
    .replaceAll("{length}", String(length))
    .replaceAll("{max}", String(maxLength));
}
